import {
  BookOpen,
  Download,
  Play,
  Calendar,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";

const Resources = () => {
  const [alertIndex, setAlertIndex] = useState(null);

  const resources = [
    {
      category: "Whitepapers",
      items: [
        {
          title: "Leadership Excellence in Africa",
          description:
            "A comprehensive guide to developing effective leadership skills in the African context.",
          type: "PDF",
          icon: Download,
          url: "resources/leadershipExcellence",
        },
        {
          title: "Organizational Transformation Strategies",
          description:
            "Best practices for driving organizational change and performance improvement.",
          type: "PDF",
          icon: Download,
          url: "resources/organizationTransformation",
        },
      ],
    },
    {
      category: "Training Guides",
      items: [
        {
          title: "Project Management Excellence Framework",
          description:
            "Step-by-step guide to implementing world-class project management practices.",
          type: "PDF",
          icon: Download,
          url: "resources/projectManagement",
        },
        {
          title: "Communication Excellence Toolkit",
          description:
            "Tools and techniques for improving communication effectiveness.",
          type: "PDF",
          icon: Download,
          url: "resources/communicationExcellence",
        },
      ],
    },
    {
      category: "Webinars",
      items: [
        {
          title: "Building High-Performance Teams",
          description:
            "Watch our expert-led session on team dynamics and performance optimization.",
          type: "Video",
          icon: Play,
        },
        {
          title: "Digital Leadership in the Modern Era",
          description:
            "Insights on leading effectively in the digital transformation age.",
          type: "Video",
          icon: Play,
        },
      ],
    },
  ];

  const upcomingEvents = [
    // {
    //   title: "Leadership Summit 2024",
    //   date: "March 15-16, 2024",
    //   location: "Nairobi, Kenya",
    //   description: "Annual gathering of African leaders and change-makers.",
    //   link: "https://example.com/register-leadership-summit",
    // },
    // {
    //   title: "Project Management Certification Workshop",
    //   date: "April 8-12, 2024",
    //   location: "Online",
    //   description: "Intensive 5-day certification program.",
    //   link: "https://example.com/register-pmc-workshop",
    // },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-white via-blue-50 to-blue-100" id="resources">
        <div className="container mx-auto px-4 text-center space-y-6">
          <h1 className="text-4xl lg:text-5xl font-bold text-blue-700">
            Resources & Learning Center
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Access our comprehensive library of training materials, guides, and
            insights to accelerate your professional development journey.
          </p>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-white via-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="space-y-12">
            {resources.map((category, catIndex) => (
              <div key={catIndex} className="space-y-6">
                <h2 className="text-2xl lg:text-3xl font-bold text-foreground flex items-center">
                  <BookOpen className="w-8 h-8 text-primary mr-3" />
                  {category.category}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {category.items.map((item, index) => {
                    const cardIndex = `${catIndex}-${index}`;
                    const isDownload = item.icon === Download && item.url;

                    return (
                      <Card
                        key={index}
                        className="shadow-md hover:shadow-xl transition-all border border-blue-100"
                      >
                        <CardHeader>
                          <CardTitle className="flex items-center justify-between">
                            <span className="text-lg font-semibold text-blue-800">
                              {item.title}
                            </span>
                            <item.icon className="w-5 h-5 text-primary" />
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <p className="text-muted-foreground text-sm">
                            {item.description}
                          </p>

                          <div className="flex items-center justify-between">
                            <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                              {item.type}
                            </span>

                            {item.url && item.url.endsWith(".pdf") ? (
                              <a href={item.url} download className="block">
                                <Button size="sm" variant="outline">
                                  Download
                                  <Download className="w-4 h-4 ml-2" />
                                </Button>
                              </a>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    setAlertIndex(
                                      alertIndex === cardIndex
                                        ? null
                                        : cardIndex
                                    )
                                  }
                                >
                                  Access Resource
                                  <ExternalLink className="w-4 h-4 ml-2" />
                                </Button>
                              </>
                            )}
                          </div>

                          {alertIndex === cardIndex && (
                            <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 rounded shadow-md animate-fade-in">
                              <div className="flex items-center">
                                <AlertCircle className="w-5 h-5 mr-2" />
                                This resource will be available soon.
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-16 lg:py-20 bg-gradient-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
              Upcoming Events
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Join our upcoming training sessions, workshops, and leadership
              events.
            </p>
          </div>

          {upcomingEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {upcomingEvents.map((event, index) => (
                <Card key={index} className="shadow-subtle">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <h3 className="text-xl font-semibold text-foreground">
                          {event.title}
                        </h3>
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4 mr-2" />
                          {event.date}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {event.location}
                        </p>
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {event.description}
                    </p>
                    <a
                      href={event.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <Button className="w-full">Register Now</Button>
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="max-w-xl mx-auto">
              <Card className="border-dashed border-2 border-blue-200 shadow-sm text-center bg-white">
                <CardContent className="p-8 space-y-4">
                  <h3 className="text-xl font-semibold text-blue-600">
                    No Upcoming Events
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    We currently have no upcoming events scheduled. Please check
                    back soon or subscribe to our newsletter for updates.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-blue-500 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center space-y-6">
          <h2 className="text-3xl lg:text-4xl font-bold">Stay Updated</h2>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Subscribe to our newsletter for the latest resources, training
            updates, and leadership insights.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="px-4 py-2 rounded-lg text-black flex-1"
            />
            <Button variant="secondary">Subscribe</Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Resources;
